import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Typography, Box } from "@mui/material";
import { noop } from "../../../utils/helpers";
import Modal from "../../../components/Modal";
import {
  NewContentForm,
  getValidationSchema,
  initialValues
} from "../../../components/Repository/AddNewContentForm";
import Tabs from "../../../components/Tabs/Tabs";
import AddFromRepositoryTable from "../../../components/CourseBuilder/AddFromRepositoryTable";
import { Formik } from "formik";
import ButtonGroup from "../../../components/Buttons/ButtonGroup";
import { useTranslation } from "react-i18next";
import Hidden from "../../../components/Hidden/Hidden";
import {
  useContentRepositorySectionItems,
  useContentRepositoryItemTypes,
  useContentRepositoryItemCategories
} from "../../../hooks/data/contentRepository";
import { useRepositoryItems } from "../../../hooks/repositoryItems";
import useUser from "../../../hooks/useUser";
import LoadingButton from "../../../components/Buttons/LoadingButton";
import { useDispatch } from "react-redux";
import { fetchResources } from "../../../redux/actions/learningCenter/resourcesActions";
import { useSnackbarNotifications } from "../../../hooks/snackbar";
import { filter } from "lodash";

const AddItemsModalActions = ({
  submitBtnText,
  onCancel,
  onSubmit,
  isSubmitting,
  submitDisabled
}) => {
  const { t } = useTranslation();
  return (
    <ButtonGroup mb={-1} mt={1}>
      <Button color="primary" onClick={onCancel}>
        {t("cancel")}
      </Button>
      <LoadingButton
        pending={isSubmitting}
        color="primary"
        variant="contained"
        onClick={onSubmit}
        data-qa="add-items-submit-btn"
        disabled={isSubmitting || submitDisabled}
      >
        {submitBtnText}
      </LoadingButton>
    </ButtonGroup>
  );
};

const AddNewContentForm = ({ onCancel, onSubmit, isSubmitting }) => {
  const { t } = useTranslation();
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema()}
      onSubmit={onSubmit}
    >
      {({ submitForm }) => (
        <>
          <Box display="flex" minHeight={0} pb={2}>
            <Box minHeight={0} overflow="hidden auto">
              <NewContentForm />
            </Box>
          </Box>
          <AddItemsModalActions
            onSubmit={submitForm}
            onCancel={onCancel}
            submitBtnText={t("resources.uploadItem")}
            isSubmitting={isSubmitting}
          />
        </>
      )}
    </Formik>
  );
};

const AddItemsModal = ({ open, onClose, currentSectionId }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isCreatingSectionItems, setIsCreatingSectionItems] = useState(false);

  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { itemTypes } = useContentRepositoryItemTypes();
  const { itemCategories } = useContentRepositoryItemCategories();
  const {
    createSectionItem,
    batchCreateSectionItems
  } = useContentRepositorySectionItems();
  const { createItem } = useRepositoryItems();
  const currentUser = useUser();
  const { displayErrorMessage } = useSnackbarNotifications();

  useEffect(() => {
    if (!open) {
      setCurrentTab(0);
    }
  }, [open]);

  const handleSelectedRowsChange = selectedRows => {
    setSelectedRows(selectedRows);
  };

  const handleAddRepositoryItems = async () => {
    setIsCreatingSectionItems(true);
    const newSectionItems = selectedRows.map(itemId => ({
      section: {
        id: currentSectionId,
        type: "section"
      },
      item: {
        id: itemId,
        type: "item"
      }
    }));
    await batchCreateSectionItems(newSectionItems);
    await dispatch(fetchResources());
    setIsCreatingSectionItems(false);
    onClose();
  };

  const handleAddNewContent = async data => {
    try {
      setIsCreatingSectionItems(true);
      const item = await createItem({
        newItem: {
          creatorId: currentUser.id,
          ...data
        },
        skipScormImport: true
      });
      if (item.id) {
        await createSectionItem({
          sectionId: currentSectionId,
          itemId: item.id
        });
        await dispatch(fetchResources());
        setIsCreatingSectionItems(false);
        onClose();
      }
    } catch (e) {
      console.error(e);
      const detailedErrors = Array.isArray(e)
        ? filter(e, errorMsg => !!errorMsg.detail)
        : [];
      detailedErrors.length > 0
        ? detailedErrors.forEach(err => displayErrorMessage(err.detail))
        : displayErrorMessage(t("resources.errorMsg.createItem"));
      setIsCreatingSectionItems(false);
    }
  };

  return (
    <Modal
      showCloseIcon
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("resources.addItems")}
        </Typography>
      }
      open={open}
      onClose={onClose}
      dialogContent={
        <React.Fragment>
          <Tabs
            tabs={["Repository", "Upload"]}
            currentTab={currentTab}
            onChange={newTab => setCurrentTab(newTab)}
            textVariant="button"
          />
          <Hidden
            hidden={currentTab === 1}
            mt={3}
            display="flex"
            flexDirection="column"
            minHeight={0}
          >
            <AddFromRepositoryTable
              onSelectRows={handleSelectedRowsChange}
              visibleColumns={["name", "repository", "contentableType"]}
              enableExpandableFilters
              itemTypes={itemTypes}
              itemCategories={itemCategories}
            />
            <AddItemsModalActions
              onSubmit={handleAddRepositoryItems}
              onCancel={onClose}
              submitBtnText={t("resources.addItems")}
              isSubmitting={isCreatingSectionItems}
              submitDisabled={currentTab == -0 && !selectedRows.length}
            />
          </Hidden>
          <Hidden
            hidden={currentTab === 0}
            mt={3}
            display="flex"
            flexDirection="column"
            minHeight={0}
          >
            <AddNewContentForm
              onCancel={onClose}
              onSubmit={handleAddNewContent}
              isSubmitting={isCreatingSectionItems}
            />
          </Hidden>
        </React.Fragment>
      }
      maxWidth="lg"
      fullWidth
      fillHeight
      padding="none"
    />
  );
};

AddItemsModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  currentSection: PropTypes.object
};

AddItemsModal.defaultProps = {
  open: false,
  onClose: noop
};

export default AddItemsModal;
